/**
 * Wallet Transactions Hook
 * Handles adding and deducting amounts from user wallets
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@finitless/shared/lib";
import { toast } from "sonner";

/**
 * Hook to handle wallet transactions (add/deduct amounts)
 * @param {number} userId - User ID
 * @returns {Object} Mutation functions for wallet operations
 */
export const useWalletTransactions = (userId) => {
  const queryClient = useQueryClient();

  // Wallet transaction mutation
  const walletTransactionMutation = useMutation({
    mutationFn: async ({ walletId, amount, description }) => {
      if (!userId || !walletId) {
        throw new Error("User ID and Wallet ID are required");
      }

      if (!amount || amount === "0") {
        throw new Error("Amount is required and must be greater than 0");
      }

      // Description is optional, but if provided, it should not be empty
      if (description && description.trim().length === 0) {
        throw new Error("Description cannot be empty if provided");
      }

      // Convert amount to string and ensure proper format
      const amountString = amount.toString();

      const response = await apiClient.post(
        `/users/${userId}/wallets/${walletId}/events`,
        {
          amount: amountString,
          description: description?.trim() || "",
        },
      );

      return response;
    },
    onSuccess: (data, variables) => {
      const { amount } = variables;
      const isDeduction = amount.startsWith("-");
      const action = isDeduction ? "deducted from" : "added to";

      toast.success("Transaction Successful", {
        description: `$${Math.abs(parseFloat(amount))} ${action} wallet successfully!`,
        duration: 3000,
      });

      // Invalidate user detail query to refresh wallet data
      queryClient.invalidateQueries({ queryKey: ["user", userId] });

      // Invalidate wallet transactions history to refresh the list
      queryClient.invalidateQueries({
        queryKey: ["wallet-transactions", userId, variables.walletId],
      });
    },
    onError: (error) => {
      console.error("Wallet transaction failed:", error);

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to process wallet transaction. Please try again.";

      toast.error("Transaction Failed", {
        description: errorMessage,
        duration: 4000,
      });
    },
  });

  return {
    walletTransactionMutation,
    isProcessing: walletTransactionMutation.isPending,
  };
};
